import type { MatterDraft } from "./ui-types";

export const ACTIVE_CAPTURE_KEY = "dicta-active-capture-v1";
export const MATTER_DRAFT_KEY = "dicta-matter-draft-v1";
export const ACTIVE_CAPTURE_CHANGE_EVENT = "dicta:active-capture-change";

const notifyActiveCaptureChange = () => {
  try {
    window.dispatchEvent(new Event(ACTIVE_CAPTURE_CHANGE_EVENT));
  } catch {
    // Non-browser tests and restricted storage contexts have no event target.
  }
};

export function hasStoredMatterDraft(): boolean {
  try {
    return localStorage.getItem(MATTER_DRAFT_KEY) !== null;
  } catch {
    return false;
  }
}

export interface ActiveCaptureMetadata {
  readonly matterId?: string;
  readonly recordingId: string;
  readonly transcriptId: string;
  readonly matterReference: string;
  readonly startedAtEpochMs: number;
}

export type LinkedActiveCaptureMetadata = ActiveCaptureMetadata & {
  readonly matterId: string;
};

export function readActiveCapture(): ActiveCaptureMetadata | null {
  try {
    const raw = localStorage.getItem(ACTIVE_CAPTURE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<ActiveCaptureMetadata>;
    if (
      typeof value.recordingId !== "string" ||
      typeof value.transcriptId !== "string" ||
      typeof value.matterReference !== "string" ||
      typeof value.startedAtEpochMs !== "number" ||
      !Number.isFinite(value.startedAtEpochMs)
    ) {
      localStorage.removeItem(ACTIVE_CAPTURE_KEY);
      return null;
    }
    return value as ActiveCaptureMetadata;
  } catch {
    return null;
  }
}

export function writeActiveCapture(value: LinkedActiveCaptureMetadata): void {
  try {
    localStorage.setItem(ACTIVE_CAPTURE_KEY, JSON.stringify(value));
    notifyActiveCaptureChange();
  } catch {
    // IndexedDB remains the evidence source if localStorage is unavailable.
  }
}

export function clearActiveCapture(): void {
  try {
    localStorage.removeItem(ACTIVE_CAPTURE_KEY);
    notifyActiveCaptureChange();
  } catch {
    // Storage cleanup is best effort; the server transcript remains canonical.
  }
}

export function readMatterDraft(fallback: MatterDraft): MatterDraft {
  try {
    const raw = localStorage.getItem(MATTER_DRAFT_KEY);
    if (!raw) return fallback;
    const value = JSON.parse(raw) as Partial<MatterDraft>;
    const authority = value.authority as
      | Partial<MatterDraft["authority"]>
      | undefined;
    if (
      typeof value.title !== "string" ||
      typeof value.reference !== "string" ||
      ![
        "Deposition / Examination",
        "Investigation / Interview",
        "Conference",
        "Hearing",
      ].includes(value.proceeding ?? "") ||
      !["GLOBAL-BASE", "AU-NSW", "US-FED-DEPOSITION"].includes(
        value.jurisdictionPack ?? "",
      ) ||
      typeof value.venue !== "string" ||
      typeof value.date !== "string" ||
      typeof value.timezone !== "string" ||
      !Array.isArray(value.languages) ||
      !value.languages.every((item) => typeof item === "string") ||
      !Array.isArray(value.glossary) ||
      !value.glossary.every((item) => typeof item === "string") ||
      !Array.isArray(value.participants) ||
      !value.participants.every(
        (item) =>
          typeof item === "object" &&
          item !== null &&
          typeof (item as { name?: unknown }).name === "string" &&
          typeof (item as { role?: unknown }).role === "string",
      ) ||
      typeof value.authorityReference !== "string" ||
      !authority ||
      typeof authority.userAttestation !== "boolean" ||
      typeof authority.participantNotice !== "boolean" ||
      typeof authority.participantConsent !== "boolean" ||
      typeof authority.confidentialityReviewed !== "boolean" ||
      typeof authority.courtApproval !== "boolean"
    ) {
      localStorage.removeItem(MATTER_DRAFT_KEY);
      return fallback;
    }
    return value as MatterDraft;
  } catch {
    return fallback;
  }
}

export function writeMatterDraft(value: MatterDraft): void {
  try {
    localStorage.setItem(MATTER_DRAFT_KEY, JSON.stringify(value));
  } catch {
    // Matter persistence is convenience; capture uses server-side matter data.
  }
}
